import React, { Fragment, useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'

import Dashboard from '@wrappers/Dashboard'
import { Title, Button } from '@lib'
import { Form, Group, Input, Submit, FormError } from '@shared/Form'
import client from '@graphql/client'
import Swal from 'sweetalert2'
import { axiosGet } from '@utils'
import axios from 'axios'
import config from '@config'

import resourceGql from '@graphql/queries/resources'
import createResource from '@graphql/mutators/create-resource'

const validateMagicBytes = (file, type) => {
  return new Promise((resolve) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const b = new Uint8Array(reader.result)
      if (type === 'application/pdf') {
        resolve(b[0] === 0x25 && b[1] === 0x50 && b[2] === 0x44 && b[3] === 0x46)
      } else if (type === 'video/mp4') {
        resolve(b[4] === 0x66 && b[5] === 0x74 && b[6] === 0x79 && b[7] === 0x70)
      } else {
        resolve(false)
      }
    }
    reader.readAsArrayBuffer(file.slice(0, 12))
  })
}

const getBucketEnv = () => {
  if (config.APP_ENV === 'prod' || config.APP_ENV === 'production') return 'prod'
  if (config.APP_ENV === 'staging' || config.APP_ENV === 'stage') return 'stage'
  return 'dev'
}

const Content = ({ user }) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [input, setInput] = useState({})
  const [inputErrors, setInputErrors] = useState({})
  const [loaderState, setLoaderState] = useState({ submit: false })

  useEffect(() => {
    if (id && loading) {
      client.query({ query: resourceGql, variables: { id } }).then(({ data }) => {
        if (data.resources) {
          const res = data.resources[0]
          setInput({
            ...res,
            logoPath: res.image,
            videoPlaceholder: res.videoFileName ? res.videoFileName.split('/').pop() : null,
            pdfPlaceholder: res.pdfFileName ? res.pdfFileName.split('/').pop() : null
          })
          setLoading(false)
        }
      })
    }
  }, [loading])

  if (loading) return null

  const handleInputChange = (field, v) => {
    setInputErrors(prev => ({ ...prev, [field]: [] }))
    if (field === 'image') {
      setInput(prev => ({ ...prev, image: v, logoPath: null, removeImage: !v && !!prev.logoPath }))
    } else if (field === 'videoFile') {
      setInput(prev => ({
        ...prev,
        videoFile: v,
        videoPlaceholder: null,
        removeVideo: !v && !!prev.videoPlaceholder
      }))
    } else if (field === 'pdfFile') {
      setInput(prev => ({
        ...prev,
        pdfFile: v,
        pdfPlaceholder: null,
        removePdf: !v && !!prev.pdfPlaceholder
      }))
    } else {
      setInput(prev => ({ ...prev, [field]: v }))
    }
  }

  const handleSave = async (evt) => {
    if (evt) evt.preventDefault()
    const feErrors = {}

    if (!input.name) feErrors.name = [{ message: 'Course name is required' }]
    if (!input.videoPlaceholder && !input.videoFile && !input.pdfPlaceholder && !input.pdfFile) {
      feErrors.file = [{ message: 'At least one file (MP4 or PDF) is required' }]
    }

    if (input.videoFile instanceof File) {
      if (input.videoFile.type !== 'video/mp4') {
        feErrors.videoFile = [{ message: 'Video must be of type .mp4' }]
      } else {
        const valid = await validateMagicBytes(input.videoFile, 'video/mp4')
        if (!valid) feErrors.videoFile = [{ message: 'File content does not match .mp4 format' }]
      }
    }

    if (input.pdfFile instanceof File) {
      if (input.pdfFile.type !== 'application/pdf') {
        feErrors.pdfFile = [{ message: 'Document must be of type .pdf' }]
      } else {
        const valid = await validateMagicBytes(input.pdfFile, 'application/pdf')
        if (!valid) feErrors.pdfFile = [{ message: 'File content does not match .pdf format' }]
      }
    }

    if (input.image instanceof File && !['image/jpeg', 'image/png'].includes(input.image.type)) {
      feErrors.imagetype = [{ message: 'Thumbnail must be of type .png, .jpg, .jpeg' }]
    }

    if (!Object.keys(feErrors).length) {
      setLoaderState(prev => ({ ...prev, submit: true }))

      const newInput = { id, name: input.name, summary: input.summary, removeVideo: !!input.removeVideo, removePdf: !!input.removePdf, removeImage: !!input.removeImage }
      if (input.videoFile instanceof File) {
        newInput.videoFile = JSON.stringify({ originalname: input.videoFile.name, mimetype: input.videoFile.type, size: input.videoFile.size })
      }
      if (input.pdfFile instanceof File) {
        newInput.pdfFile = JSON.stringify({ originalname: input.pdfFile.name, mimetype: input.pdfFile.type, size: input.pdfFile.size })
      }
      if (input.image instanceof File) {
        newInput.image = JSON.stringify({ originalname: input.image.name, mimetype: input.image.type, size: input.image.size })
      }

      createResource(newInput).then(async response => {
        if (response.data.createResource) {
          const bucket = `xevant-${getBucketEnv()}-alcatraz-be-uploads-us-west-2`
          const res = response.data.createResource
          const uploads = []

          if (input.videoFile instanceof File && res.videoFileName) {
            uploads.push(axiosGet(`s3/signed-url/${bucket}/${encodeURIComponent('mediaFiles/' + res.videoFileName)}?contentType=video%2Fmp4`).then(e => axios.put(e.data.url, input.videoFile, { headers: { 'Content-Type': 'video/mp4' } })))
          }
          if (input.pdfFile instanceof File && res.pdfFileName) {
            uploads.push(axiosGet(`s3/signed-url/${bucket}/${encodeURIComponent('mediaFiles/' + res.pdfFileName)}?contentType=application%2Fpdf`).then(e => axios.put(e.data.url, input.pdfFile, { headers: { 'Content-Type': 'application/pdf' } })))
          }
          if (input.image instanceof File && res.image) {
            uploads.push(axiosGet(`s3/signed-url/${bucket}/${encodeURIComponent('resourceUploads/images/' + res.image)}`).then(e => axios.put(e.data.url, input.image)))
          }

          await Promise.all(uploads)
          Swal.fire({ text: 'Course updated', icon: 'success', showConfirmButton: false, timer: 3000 })
          navigate('/settings/training-courses')
        }
      })
    }
    setInputErrors(feErrors)
  }

  return (
    <Fragment>
      <Title text='Edit Training Course' user={user}/>

      <Form className='course-upload-form' onSubmit={handleSave}>
        <FormError errors={inputErrors} />
        <Group>
          <Input
            label='Course Name'
            type='text'
            value={input.name}
            onChange={(v) => handleInputChange('name', v)}
            errors={inputErrors.name}
          />
        </Group>
        <div className={`course-files-card${inputErrors.file ? ' has-error' : ''}`}>
          <div className='course-files-card-header'>
            <span className='course-files-label'>Course Content <span className='required'>*</span></span>
            <span className='course-files-hint'>At least one is required. Both can be uploaded.</span>
          </div>
          {inputErrors.file && <p className='course-files-error'>{inputErrors.file[0].message}</p>}
          <div className='course-files-row'>
            <Group>
              <Input
                label='Upload Video'
                type='draganddrop'
                placeholder='upload as .mp4'
                value={input.videoFile}
                onChange={(v) => handleInputChange('videoFile', v.target.files[0])}
                accept='.mp4'
                filePlaceHolder={input.videoPlaceholder}
                className='resource-upload-video'
                errors={inputErrors.videoFile}
              />
            </Group>
            <Group>
              <Input
                label='Upload PDF'
                type='draganddrop'
                placeholder='upload as .pdf'
                value={input.pdfFile}
                onChange={(v) => handleInputChange('pdfFile', v.target.files[0])}
                accept='.pdf'
                filePlaceHolder={input.pdfPlaceholder}
                className='resource-upload-pdf'
                errors={inputErrors.pdfFile}
              />
            </Group>
          </div>
        </div>
        <Group>
          <Input
            label={<>Upload Thumbnail Image <span className='optional'>(Optional)</span></>}
            type='draganddrop'
            value={input.image}
            onChange={(v) => handleInputChange('image', v.target.files[0])}
            accept='.png, .jpg, .jpeg'
            logoDirectory='resource-image'
            logoPath={input.logoPath}
            className='thumbnail-upload'
            errors={inputErrors.imagetype}
          />
        </Group>
        <Group>
          <Input
            label={<>Course Summary <span className='optional'>(Optional)</span></>}
            type='textarea'
            value={input.summary}
            onChange={(v) => handleInputChange('summary', v)}
          />
        </Group>

        <Submit>
          <Button
            type='submit'
            text='Save Course'
            icon='fas fa-arrow-alt-to-top'
            iconAlign='left'
            className='icon-left primary full-width'
            loading={loaderState.submit}
          />
        </Submit>
      </Form>
    </Fragment>
  )
}

Content.propTypes = {
  user: PropTypes.object
}

const EditResource = () => {
  return (
    <Dashboard>
      <Content />
    </Dashboard>
  )
}

export default EditResource
